import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { Icon } from '../components/Brand';
import { PageBody, PageHead } from '../components/Page';
import { Badge, type Tone } from '../components/Pill';
import { StatusBanner } from '../components/StatusBanner';
import { dateFnsLocale, formatCount } from '../i18n/formats';
import { useLocale } from '../i18n/LocaleProvider';
import {
  AUDIT_PAGE_SIZE,
  CROSS_SHOP_DOMAIN,
  EMPTY_AUDIT_FILTERS,
  auditActionLabelKey,
  auditFilterOptions,
  auditOutcome,
  auditPageCount,
  auditParamsFromState,
  auditQuery,
  auditShopLink,
  auditStateFromParams,
  formatAuditPayload,
  isDateRangeValid,
  isShopDomainFilterValid,
  type AuditFilterForm,
  type AuditOutcome,
  type AuditUrlState,
} from '../lib/audit-log';
import {
  fetchAuditEntry,
  fetchAuditLog,
  opsErrorMessage,
  type OperatorAuditRow,
} from '../lib/ops-api';

/** Pill tone per recorded outcome. The token itself is shown raw (API enum, not translated). */
const OUTCOME_TONE: Record<AuditOutcome, Tone> = {
  pending: 'amber',
  succeeded: 'green',
  failed: 'red',
};

/** Timestamp the way the job drawer prints one; the locale is what turns `Mar` into `Mär`. */
function useTimestamp(): (iso: string) => string {
  const { locale } = useLocale();
  return (iso) =>
    format(new Date(iso), 'dd MMM yyyy HH:mm:ss', {
      locale: dateFnsLocale(locale),
    });
}

/** An action token, raw, with its friendly label where the catalogue has one. */
function ActionLabel({ action }: { readonly action: string }) {
  const { t } = useLocale();
  const key = auditActionLabelKey(action);
  const label = t(key);
  return (
    <>
      {label !== key && <span className="block text-gray-900">{label}</span>}
      <span className="block font-mono text-xs font-normal text-gray-500">
        {action}
      </span>
    </>
  );
}

/** A row's shop: a link where there is somewhere to go, a badge for cross-shop rows. */
function ShopCell({
  row,
}: {
  readonly row: {
    readonly shopId?: string | null;
    readonly shopDomain: string;
  };
}) {
  const { t } = useLocale();
  if (row.shopDomain === CROSS_SHOP_DOMAIN) {
    return <Badge tone="gray">{t('auditLog.crossShop')}</Badge>;
  }
  const link = auditShopLink(row);
  return link ? (
    <Link to={link} className="font-mono text-xs underline">
      {row.shopDomain}
    </Link>
  ) : (
    <span className="font-mono text-xs">{row.shopDomain}</span>
  );
}

/**
 * The operator audit log (work order item 10, AC-10.1 / AC-10.2): what each operator did, and why.
 *
 * **Read-only by construction** — no edit, no delete, because the rows are
 * Verfahrensdokumentation; the API has no route that could change one. With no role model (D11)
 * this page is how the operators see each other's work, which is the control the AVV §11.1
 * "nur anlassbezogen" claim rests on.
 *
 * The filters are the API's own — operator, action, exact shop domain (or `*` for cross-shop
 * rows), date range — each an equality or range seek on an existing index, and their dropdown
 * values come from what the log contains. The list carries no payloads; opening a row loads its
 * payload and is itself recorded (`audit.entry.view`), so it happens on click only.
 */
export function AuditLogPage() {
  const { t, locale } = useLocale();
  const timestamp = useTimestamp();
  const [searchParams, setSearchParams] = useSearchParams();
  const state = auditStateFromParams(searchParams);
  const [draft, setDraft] = useState<AuditFilterForm>(state.form);

  // Follow the URL when it changes under the page (back button, a link from a success banner).
  const urlKey = searchParams.toString();
  useEffect(() => {
    setDraft(auditStateFromParams(new URLSearchParams(urlKey)).form);
  }, [urlKey]);

  const navigate = (next: AuditUrlState) =>
    setSearchParams(auditParamsFromState(next));

  const shopValid = isShopDomainFilterValid(draft.shopDomain);
  const rangeValid = isDateRangeValid(draft.from, draft.to);

  const list = useQuery({
    queryKey: ['audit-log', state.form, state.page],
    queryFn: () => fetchAuditLog(auditQuery(state.form, state.page)),
    placeholderData: keepPreviousData,
    // A URL typed by hand can carry a filter the API refuses; do not send it.
    enabled:
      isShopDomainFilterValid(state.form.shopDomain) &&
      isDateRangeValid(state.form.from, state.form.to),
  });

  function apply(event: FormEvent): void {
    event.preventDefault();
    if (!shopValid || !rangeValid) return;
    navigate({ form: draft, page: 1, entry: null });
  }

  const rows = list.data?.data ?? [];
  const pages = auditPageCount(list.data?.total ?? 0, AUDIT_PAGE_SIZE);

  return (
    <>
      <PageHead
        eyebrow={t('auditLog.eyebrow')}
        title={t('auditLog.title')}
        lead={t('auditLog.lead')}
      />
      <PageBody>
        <form
          onSubmit={apply}
          className="surface p-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
        >
          <div>
            <label htmlFor="audit-operator" className="field-label">
              {t('auditLog.filters.operator')}
            </label>
            <select
              id="audit-operator"
              value={draft.operator}
              onChange={(e) => setDraft({ ...draft, operator: e.target.value })}
              className="field-input"
            >
              <option value="">{t('auditLog.filters.any')}</option>
              {auditFilterOptions(
                list.data?.operators ?? [],
                draft.operator,
              ).map((operator) => (
                <option key={operator} value={operator}>
                  {operator}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="audit-action" className="field-label">
              {t('auditLog.filters.action')}
            </label>
            <select
              id="audit-action"
              value={draft.action}
              onChange={(e) => setDraft({ ...draft, action: e.target.value })}
              className="field-input font-mono"
            >
              <option value="">{t('auditLog.filters.any')}</option>
              {auditFilterOptions(list.data?.actions ?? [], draft.action).map(
                (action) => (
                  <option key={action} value={action}>
                    {action}
                  </option>
                ),
              )}
            </select>
          </div>
          <div>
            <label htmlFor="audit-shop" className="field-label">
              {t('auditLog.filters.shop')}
            </label>
            <input
              id="audit-shop"
              type="text"
              value={draft.shopDomain}
              onChange={(e) =>
                setDraft({ ...draft, shopDomain: e.target.value })
              }
              placeholder={t('auditLog.filters.shopPlaceholder')}
              aria-invalid={!shopValid}
              className="field-input font-mono"
            />
            <p className={shopValid ? 'field-hint' : 'field-error'}>
              {shopValid
                ? t('auditLog.filters.shopHint')
                : t('auditLog.filters.shopInvalid')}
            </p>
          </div>
          <div>
            <label htmlFor="audit-from" className="field-label">
              {t('auditLog.filters.from')}
            </label>
            <input
              id="audit-from"
              type="date"
              value={draft.from}
              onChange={(e) => setDraft({ ...draft, from: e.target.value })}
              className="field-input"
            />
          </div>
          <div>
            <label htmlFor="audit-to" className="field-label">
              {t('auditLog.filters.to')}
            </label>
            <input
              id="audit-to"
              type="date"
              value={draft.to}
              onChange={(e) => setDraft({ ...draft, to: e.target.value })}
              aria-invalid={!rangeValid}
              className="field-input"
            />
            {!rangeValid && (
              <p className="field-error">
                {t('auditLog.filters.rangeInvalid')}
              </p>
            )}
          </div>
          <div className="flex items-end gap-2">
            <button
              type="submit"
              className="btn-primary btn-sm"
              disabled={!shopValid || !rangeValid}
            >
              {t('auditLog.filters.apply')}
            </button>
            <button
              type="button"
              className="btn-neutral btn-sm"
              onClick={() =>
                navigate({ form: EMPTY_AUDIT_FILTERS, page: 1, entry: null })
              }
            >
              {t('auditLog.filters.reset')}
            </button>
          </div>
        </form>

        {list.isError && (
          <StatusBanner tone="error">
            {opsErrorMessage(list.error, t('auditLog.failed'))}
          </StatusBanner>
        )}

        {list.data && rows.length === 0 && (
          <StatusBanner tone="info">{t('auditLog.empty')}</StatusBanner>
        )}

        {rows.length > 0 && (
          <div className="surface overflow-x-auto">
            {/* Cells carry the padding the shared `.data-table` recipe leaves to each table — this
                one had none, so its first column sat flush against the panel's edge. Below `sm`
                the row folds into its first cell: who did what, to which shop and why, with the
                "open" link kept in its own narrow column. */}
            <table className="data-table table-fixed sm:table-auto sm:min-w-[720px] [&_td]:px-4 [&_td]:py-3 [&_th]:px-4 [&_th]:py-3">
              <thead>
                <tr>
                  <th>{t('auditLog.columns.when')}</th>
                  <th className="hidden sm:table-cell">
                    {t('auditLog.columns.operator')}
                  </th>
                  <th className="hidden sm:table-cell">
                    {t('auditLog.columns.action')}
                  </th>
                  <th className="hidden sm:table-cell">
                    {t('auditLog.columns.shop')}
                  </th>
                  <th className="hidden sm:table-cell">
                    {t('auditLog.columns.reason')}
                  </th>
                  <th className="w-24 sm:w-auto" />
                </tr>
              </thead>
              <tbody>
                {rows.map((row: OperatorAuditRow) => (
                  <tr key={row.id}>
                    <td className="text-xs text-gray-600 whitespace-nowrap tabular-nums">
                      {timestamp(row.createdAt)}
                      <div className="sm:hidden mt-1 space-y-1 whitespace-normal text-sm">
                        <div className="truncate text-gray-900">
                          {row.operator}
                        </div>
                        <div className="break-all">
                          <ActionLabel action={row.action} />
                        </div>
                        <div className="break-all">
                          <ShopCell row={row} />
                        </div>
                        <div className="text-gray-700 truncate">
                          {row.reason}
                        </div>
                      </div>
                    </td>
                    <td className="hidden sm:table-cell text-sm">
                      {row.operator}
                    </td>
                    <td className="hidden sm:table-cell">
                      <ActionLabel action={row.action} />
                    </td>
                    <td className="hidden sm:table-cell">
                      <ShopCell row={row} />
                    </td>
                    <td className="hidden sm:table-cell text-sm text-gray-700 max-w-sm truncate">
                      {row.reason}
                    </td>
                    <td className="text-right">
                      <button
                        type="button"
                        className="link-arrow"
                        onClick={() => navigate({ ...state, entry: row.id })}
                      >
                        {t('auditLog.open')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {list.data && pages > 1 && (
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
            <p className="text-sm text-gray-600">
              {t('auditLog.pagination', {
                page: state.page,
                pages,
                total: formatCount(list.data.total, locale),
              })}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={state.page <= 1}
                onClick={() =>
                  navigate({ ...state, page: state.page - 1, entry: null })
                }
                className="btn-neutral btn-sm"
              >
                {t('common.prev')}
              </button>
              <button
                type="button"
                disabled={state.page >= pages}
                onClick={() =>
                  navigate({ ...state, page: state.page + 1, entry: null })
                }
                className="btn-neutral btn-sm"
              >
                {t('common.next')}
              </button>
            </div>
          </div>
        )}
      </PageBody>

      {state.entry && (
        <AuditEntryDrawer
          id={state.entry}
          onClose={() => navigate({ ...state, entry: null })}
        />
      )}
    </>
  );
}

/**
 * One entry with its payload, loaded when the drawer opens — which the API records as an
 * `audit.entry.view` row, so the fetch is never cached across openings (`gcTime: 0`): a second
 * look is a second read and gets its own row.
 */
function AuditEntryDrawer({
  id,
  onClose,
}: {
  readonly id: string;
  readonly onClose: () => void;
}) {
  const { t } = useLocale();
  const timestamp = useTimestamp();
  const entry = useQuery({
    queryKey: ['audit-entry', id],
    queryFn: () => fetchAuditEntry(id),
    gcTime: 0,
    staleTime: Infinity,
    retry: false,
  });

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const outcome = entry.data ? auditOutcome(entry.data.payload) : null;

  return (
    <>
      <div
        className="fixed inset-0 bg-gray-900/40 z-40 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('auditLog.drawer.title')}
        className="fixed right-0 top-0 h-full w-full sm:max-w-2xl bg-white border-l border-gray-200 z-50 flex flex-col shadow-2xl"
      >
        <div className="flex items-start justify-between px-4 sm:px-6 py-4 sm:py-5 border-b border-gray-200 bg-gradient-to-b from-primary-50/60 to-white">
          <div className="min-w-0">
            <p className="font-mono text-xs text-gray-500 mb-1">{id}</p>
            <h2 className="text-lg font-bold text-gray-900 truncate">
              {entry.data ? (
                <ActionLabel action={entry.data.action} />
              ) : (
                t('auditLog.drawer.title')
              )}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="ml-4 flex-shrink-0 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg p-2 min-w-[44px] min-h-[44px] flex items-center justify-center transition-colors"
            aria-label={t('common.close')}
          >
            <Icon name="close" className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-auto px-4 sm:px-6 py-4 space-y-4">
          {entry.isLoading && (
            <p className="text-gray-500 text-sm">{t('common.loading')}</p>
          )}
          {entry.isError && (
            <StatusBanner tone="error">
              {opsErrorMessage(entry.error, t('auditLog.drawer.failed'))}
            </StatusBanner>
          )}
          {entry.data && (
            <>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <dt className="text-gray-500 text-xs mb-1">
                    {t('auditLog.columns.when')}
                  </dt>
                  <dd className="text-gray-900 tabular-nums">
                    {timestamp(entry.data.createdAt)}
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500 text-xs mb-1">
                    {t('auditLog.columns.operator')}
                  </dt>
                  <dd className="text-gray-900">{entry.data.operator}</dd>
                </div>
                <div>
                  <dt className="text-gray-500 text-xs mb-1">
                    {t('auditLog.columns.shop')}
                  </dt>
                  <dd>
                    <ShopCell row={entry.data} />
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500 text-xs mb-1">
                    {t('auditLog.drawer.target')}
                  </dt>
                  <dd className="font-mono text-xs text-gray-900 break-all">
                    {entry.data.targetType}
                    {entry.data.targetId ? ` · ${entry.data.targetId}` : ''}
                  </dd>
                </div>
                {outcome && (
                  <div>
                    <dt className="text-gray-500 text-xs mb-1">
                      {t('auditLog.drawer.outcome')}
                    </dt>
                    <dd>
                      <Badge tone={OUTCOME_TONE[outcome]}>{outcome}</Badge>
                    </dd>
                  </div>
                )}
              </dl>
              {outcome === 'pending' && (
                <StatusBanner tone="warning">
                  {t('auditLog.drawer.stillPending')}
                </StatusBanner>
              )}
              <div>
                <p className="text-gray-500 text-xs mb-1">
                  {t('auditLog.columns.reason')}
                </p>
                <p className="text-sm text-gray-900 whitespace-pre-wrap break-words">
                  {entry.data.reason}
                </p>
              </div>
              <div>
                <p className="text-gray-500 text-xs mb-1">
                  {t('auditLog.drawer.payload')}
                </p>
                <pre className="text-xs text-gray-800 font-mono whitespace-pre-wrap break-words bg-gray-50 border border-gray-200 rounded-lg p-4 overflow-auto">
                  {formatAuditPayload(entry.data.payload)}
                </pre>
              </div>
              <p className="text-xs text-gray-500">
                {t('auditLog.drawer.viewRecorded')}
              </p>
            </>
          )}
        </div>
      </div>
    </>
  );
}
